import { useCallback, useEffect, useRef, useState } from "react";
import { useReactFlow, useStoreApi, type Connection } from "@xyflow/react";
import { nanoid } from "nanoid";
import { absolutePosition } from "@/lib/auto-fuse";
import { GEOMETRY } from "@/lib/canvas-geometry";
import { getDefinition } from "@/lib/catalog";
import { resolveDropTargetId } from "@/lib/drop-target";
import { effectiveTerminals } from "@/lib/effective";
import { isJunction } from "@/lib/node-kind";
import { useSystemStore } from "@/lib/store";
import {
  inferJunctionPolarity,
  nearestSidePlacement,
  pickSnapTerminal,
  terminalAbsPosition,
  terminalPolarity,
} from "@/lib/terminal-snap";
import type {
  System,
  SystemNode,
  Terminal,
} from "@/lib/types";
import {
  flowHitTolerance,
  storedTerminalId,
} from "@/lib/canvas-interaction";
import type { ConnectionPreview } from "./connection-preview";

const { JUNCTION_HALF } = GEOMETRY;

// Slop when hit-testing a drop: without it, releasing a few pixels off a node
// falls into the empty-pane branch and mints a stray junction.
const DROP_HIT_TOLERANCE_PX = 24;
const CABLE_HIT_TOLERANCE_PX = 16;

type Pt = { x: number; y: number };

/** Project `p` onto segment a→b; the returned `t` is clamped to 0..1. */
function projectOnSegment(p: Pt, a: Pt, b: Pt): { point: Pt; t: number; dist: number } {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const apx = p.x - a.x;
  const apy = p.y - a.y;
  const len2 = abx * abx + aby * aby;
  let t = len2 === 0 ? 0 : (apx * abx + apy * aby) / len2;
  t = Math.max(0, Math.min(1, t));
  const point = { x: a.x + abx * t, y: a.y + aby * t };
  return { point, t, dist: Math.hypot(p.x - point.x, p.y - point.y) };
}

/**
 * Cable closest to `cursorFlow`, read off the transparent hit strips
 * `CableEdgeView` renders — their coords are already in flow space. `fraction`
 * is arc-length along the polyline, for splitting `lengthM` in proportion.
 */
function findCableHit(
  cursorFlow: Pt,
  excludeCableIds: Set<string>,
  threshold = 16,
): { cableId: string; point: Pt; fraction: number } | null {
  const edges = document.querySelectorAll<SVGGElement>(".react-flow__edge");
  let best: { cableId: string; point: Pt; fraction: number } | null = null;
  let bestDist = threshold;
  for (const edge of Array.from(edges)) {
    const cableId = edge.getAttribute("data-id");
    if (!cableId || excludeCableIds.has(cableId)) continue;
    const lines = edge.querySelectorAll<SVGLineElement>('line[stroke="transparent"]');
    if (lines.length === 0) continue;
    type Seg = { a: Pt; b: Pt; len: number };
    const segs: Seg[] = [];
    let total = 0;
    for (const line of Array.from(lines)) {
      const a = {
        x: parseFloat(line.getAttribute("x1") ?? "0"),
        y: parseFloat(line.getAttribute("y1") ?? "0"),
      };
      const b = {
        x: parseFloat(line.getAttribute("x2") ?? "0"),
        y: parseFloat(line.getAttribute("y2") ?? "0"),
      };
      const len = Math.hypot(b.x - a.x, b.y - a.y);
      segs.push({ a, b, len });
      total += len;
    }
    let cum = 0;
    for (const seg of segs) {
      const proj = projectOnSegment(cursorFlow, seg.a, seg.b);
      if (proj.dist < bestDist) {
        bestDist = proj.dist;
        const fraction = total > 0 ? (cum + proj.t * seg.len) / total : 0.5;
        best = { cableId, point: proj.point, fraction };
      }
      cum += seg.len;
    }
  }
  return best;
}

/** Signal/data stud? A junction never is — it carries whatever rail it's on. */
function isSignalEndpoint(
  nodeId: string,
  terminalId: string,
  nodes: ReadonlyArray<SystemNode>,
): boolean {
  const node = nodes.find((n) => n.id === nodeId);
  if (!node || node.kind !== "component") return false;
  const def = getDefinition(node.definitionId, node.customDefinition);
  if (!def) return false;
  return def.terminals.find((t) => t.id === terminalId)?.polarity === "signal";
}

function isSignalConnection(
  fromNodeId: string,
  fromTerminalId: string,
  toNodeId: string,
  toTerminalId: string,
  nodes: ReadonlyArray<SystemNode>,
): boolean {
  return (
    isSignalEndpoint(fromNodeId, fromTerminalId, nodes) ||
    isSignalEndpoint(toNodeId, toTerminalId, nodes)
  );
}

function cablesTouching(
  nodeId: string,
  cables: Array<{ id: string; fromNodeId: string; toNodeId: string }>,
): Set<string> {
  const set = new Set<string>();
  for (const c of cables) {
    if (c.fromNodeId === nodeId || c.toNodeId === nodeId) set.add(c.id);
  }
  return set;
}

function connectionSource(source: SystemNode, terminalId: string, system: System) {
  if (isJunction(source)) {
    const abs = absolutePosition(source, system.nodes);
    return {
      sourcePoint: { x: abs.x + JUNCTION_HALF, y: abs.y + JUNCTION_HALF },
      sourcePolarity: inferJunctionPolarity(source.id, system.nodes, system.cables),
      sourceTerm: null,
    };
  }
  if (source.kind !== "component") return null;
  const def = getDefinition(source.definitionId, source.customDefinition);
  if (!def) return null;
  const sourceTerm = effectiveTerminals(def, source).find(t => t.id === terminalId);
  if (!sourceTerm) return null;
  return {
    sourcePoint: terminalAbsPosition(source, def, sourceTerm, system.nodes),
    sourcePolarity: terminalPolarity(sourceTerm),
    sourceCategory: def.category,
    sourceTerminalPolarity: sourceTerm.polarity,
    sourceTerminalDirection: sourceTerm.direction,
    sourceTerminalId: terminalId,
    sourceTerm,
  };
}

export function useCanvasConnect() {
  const { screenToFlowPosition, getIntersectingNodes, getZoom } = useReactFlow();
  const reactFlowStore = useStoreApi();

  const system = useSystemStore((s) => s.system);
  const setPendingConnection = useSystemStore((s) => s.setPendingConnection);
  const addCable = useSystemStore((s) => s.addCable);
  const addJunction = useSystemStore((s) => s.addJunction);
  const splitCableWithJunction = useSystemStore((s) => s.splitCableWithJunction);
  const customizeComponent = useSystemStore((s) => s.customizeComponent);

  const [cableTargetId, setCableTargetId] = useState<string | null>(null);
  // The cable under the cursor: dropping splits it with a junction.
  const [cableTargetEdgeId, setCableTargetEdgeId] = useState<string | null>(null);
  const [connectionPreview, setConnectionPreview] = useState<ConnectionPreview | null>(null);
  const connectFromIdRef = useRef<string | null>(null);
  const connectFromTerminalRef = useRef<string | null>(null);
  const connectionCancelledRef = useRef(false);

  const onConnectStart = useCallback(
    (_event: unknown, params: { nodeId?: string | null; handleId?: string | null }) => {
      connectionCancelledRef.current = false;
      connectFromIdRef.current = params.nodeId ?? null;
      connectFromTerminalRef.current = params.handleId ?? null;
      setConnectionPreview(null);
    },
    [],
  );

  const cancelConnectionGesture = useCallback(() => {
    // This may run before XYFlow crosses its drag threshold and calls
    // onConnectStart. Mark the session cancelled unconditionally so the first
    // finger's eventual touchend cannot mint a provisional junction.
    connectionCancelledRef.current = true;
    connectFromIdRef.current = null;
    setCableTargetId(null);
    setCableTargetEdgeId(null);
    setConnectionPreview(null);
    reactFlowStore.getState().cancelConnection();
  }, [reactFlowStore]);

  useEffect(() => {
    document.addEventListener("touchcancel", cancelConnectionGesture, true);
    return () =>
      document.removeEventListener("touchcancel", cancelConnectionGesture, true);
  }, [cancelConnectionGesture]);

  // `onConnectEnd` repeats these hit-tests to commit the drop — keep them in step.
  useEffect(() => {
    const handler = (e: PointerEvent) => {
      const fromId = connectFromIdRef.current;
      if (fromId === null) return;
      const flow = screenToFlowPosition(
        { x: e.clientX, y: e.clientY },
        { snapToGrid: false },
      );
      let hits = getIntersectingNodes({ x: flow.x, y: flow.y, width: 1, height: 1 });
      let id = resolveDropTargetId(hits, flow);
      if (!id) {
        const tolerance = flowHitTolerance(DROP_HIT_TOLERANCE_PX, getZoom());
        const half = tolerance / 2;
        hits = getIntersectingNodes({
          x: flow.x - half,
          y: flow.y - half,
          width: tolerance,
          height: tolerance,
        });
        id = resolveDropTargetId(hits, flow);
      }
      const nextNode = id && id !== fromId ? id : null;
      setCableTargetId((prev) => (prev === nextNode ? prev : nextNode));
      let preview: ConnectionPreview | null = null;
      const source = system.nodes.find(node => node.id === fromId);
      const target = system.nodes.find(node => node.id === nextNode);
      const terminalId = connectFromTerminalRef.current;
      const sourceInfo = source && terminalId ? connectionSource(source, terminalId, system) : null;
      // Signal body drops can create a new colour-matched terminal. Keep that
      // separate flow unchanged; this preview covers existing power terminals.
      if (sourceInfo && sourceInfo.sourceTerm?.polarity !== "signal" && target?.kind === "component" && !isJunction(target)) {
        const targetDef = getDefinition(target.definitionId, target.customDefinition);
        const terminal = targetDef && pickSnapTerminal({
          ...sourceInfo, targetPoint: flow, target, targetDef, nodes: system.nodes, cables: system.cables,
        });
        if (targetDef && terminal) preview = {
          nodeId: target.id,
          terminalId: terminal.id,
          point: terminalAbsPosition(target, targetDef, terminal, system.nodes),
          side: terminal.side,
        };
      }
      setConnectionPreview(prev =>
        prev?.nodeId === preview?.nodeId && prev?.terminalId === preview?.terminalId &&
        prev?.point.x === preview?.point.x && prev?.point.y === preview?.point.y && prev?.side === preview?.side
          ? prev : preview);
      // A node always wins the target slot; only look for a cable when none did.
      const exclude = cablesTouching(fromId, system.cables);
      const nextEdge = nextNode
        ? null
        : findCableHit(
            flow,
            exclude,
            flowHitTolerance(CABLE_HIT_TOLERANCE_PX, getZoom()),
          )?.cableId ?? null;
      setCableTargetEdgeId((prev) => (prev === nextEdge ? prev : nextEdge));
    };
    document.addEventListener("pointermove", handler);
    return () => document.removeEventListener("pointermove", handler);
  }, [screenToFlowPosition, getIntersectingNodes, getZoom, system]);

  const onConnect = useCallback(
    (conn: Connection) => {
      if (!conn.source || !conn.target || !conn.sourceHandle || !conn.targetHandle) return;
      if (conn.source === conn.target) return;
      // A junction has no specs to size against, so skip the length+fuse modal.
      const fromNode = system.nodes.find((n) => n.id === conn.source);
      const toNode = system.nodes.find((n) => n.id === conn.target);
      const fromIsJ = isJunction(fromNode);
      const toIsJ = isJunction(toNode);
      if (fromIsJ || toIsJ) {
        const cableId = addCable({
          fromNodeId: conn.source,
          // The stored junction handle is always "j"; `rfEdges` picks the
          // cardinal one at render time.
          fromTerminalId: storedTerminalId(fromIsJ, conn.sourceHandle),
          toNodeId: conn.target,
          toTerminalId: storedTerminalId(toIsJ, conn.targetHandle),
          lengthM: 0.5,
        });
        useSystemStore.getState().setSelection({ nodeIds: [], cableIds: [cableId] });
        return;
      }
      // Signal wiring has nothing to size or fuse, so it skips the modal too.
      if (
        isSignalConnection(
          conn.source,
          conn.sourceHandle,
          conn.target,
          conn.targetHandle,
          system.nodes,
        )
      ) {
        const cableId = addCable({
          fromNodeId: conn.source,
          fromTerminalId: conn.sourceHandle,
          toNodeId: conn.target,
          toTerminalId: conn.targetHandle,
          lengthM: 0.5,
        });
        useSystemStore.getState().setSelection({ nodeIds: [], cableIds: [cableId] });
        return;
      }
      setPendingConnection({
        fromNodeId: conn.source,
        fromTerminalId: conn.sourceHandle,
        toNodeId: conn.target,
        toTerminalId: conn.targetHandle,
      });
    },
    [setPendingConnection, system.nodes, addCable],
  );

  /** Fired when the user releases a connection drag; a drop on a target handle
   *  has already been taken by `onConnect`. */
  const onConnectEnd = useCallback(
    (
      event: MouseEvent | TouchEvent,
      connectionState: {
        isValid: boolean | null;
        fromNode?: { id?: string } | null;
        fromHandle?: { id?: string | null } | null;
        toNode?: { id?: string } | null;
      },
    ) => {
      if (connectionCancelledRef.current) {
        connectionCancelledRef.current = false;
        connectFromIdRef.current = null;
        setCableTargetId(null);
        setCableTargetEdgeId(null);
        setConnectionPreview(null);
        return;
      }
      const fromIdAtStart = connectFromIdRef.current;
      connectFromIdRef.current = null;
      setCableTargetId(null);
      setCableTargetEdgeId(null);
      setConnectionPreview(null);
      if (connectionState.isValid) return;
      const fromNodeId = connectionState.fromNode?.id;
      const rawFromTerminalId = connectionState.fromHandle?.id;
      if (!fromNodeId || !rawFromTerminalId) return;
      // The touch-only branch handle is a UI affordance. Junction endpoints are
      // always persisted as `j`, just like direct handle-to-handle connections.
      const fromTerminalId = storedTerminalId(
        isJunction(system.nodes.find((node) => node.id === fromNodeId)),
        rawFromTerminalId,
      );
      const cx = "clientX" in event ? event.clientX : event.changedTouches?.[0]?.clientX;
      const cy = "clientY" in event ? event.clientY : event.changedTouches?.[0]?.clientY;
      const dropPoint = cx != null && cy != null
        ? screenToFlowPosition({ x: cx, y: cy }, { snapToGrid: false })
        : undefined;

      // React Flow sets `toNode` only for a drop over a registered handle, not
      // over the node body — hit-test the cursor so a body drop still resolves.
      let toNodeId = connectionState.toNode?.id;
      if (!toNodeId) {
        if (dropPoint) {
          const flow = dropPoint;
          let hits = getIntersectingNodes({ x: flow.x, y: flow.y, width: 1, height: 1 });
          toNodeId = resolveDropTargetId(hits, flow);
          if (!toNodeId) {
            const tolerance = flowHitTolerance(
              DROP_HIT_TOLERANCE_PX,
              getZoom(),
            );
            const half = tolerance / 2;
            hits = getIntersectingNodes({
              x: flow.x - half,
              y: flow.y - half,
              width: tolerance,
              height: tolerance,
            });
            toNodeId = resolveDropTargetId(hits, flow);
          }
        }
      }

      if (toNodeId && toNodeId !== fromNodeId) {
        const target = system.nodes.find((n) => n.id === toNodeId);
        const source = system.nodes.find((n) => n.id === fromNodeId);
        if (!target || !source) return;

        const targetIsJ = isJunction(target);
        const sourceIsJ = isJunction(source);

        if (targetIsJ) {
          const cableId = addCable({
            fromNodeId,
            fromTerminalId: sourceIsJ ? "j" : fromTerminalId,
            toNodeId,
            toTerminalId: "j",
            lengthM: 0.5,
          });
          useSystemStore.getState().setSelection({ nodeIds: [], cableIds: [cableId] });
          return;
        }
        if (target.kind !== "component") return;

        const targetDef = getDefinition(target.definitionId, target.customDefinition);
        if (!targetDef) return;

        const sourceInfo = connectionSource(source, fromTerminalId, system);
        if (!sourceInfo) return;
        const { sourcePoint, sourceTerm } = sourceInfo;

        // Each signal colour is its own channel: snap to an unwired stud of the
        // same colour, or mint one. Nearest-terminal snapping lands CAN-H on a
        // + post.
        if (sourceTerm && sourceTerm.polarity === "signal") {
          const all = effectiveTerminals(targetDef, target);
          const wired = new Set<string>();
          for (const c of system.cables) {
            if (c.fromNodeId === toNodeId) wired.add(c.fromTerminalId);
            if (c.toNodeId === toNodeId) wired.add(c.toTerminalId);
          }
          const existing = all.find(
            (t) =>
              t.polarity === "signal" &&
              (!sourceTerm!.color || t.color === sourceTerm!.color) &&
              !wired.has(t.id),
          );
          useSystemStore.getState().pushHistory();
          let toTerminalId: string;
          if (existing) {
            toTerminalId = existing.id;
          } else {
            const placement = nearestSidePlacement(target, targetDef, system.nodes, sourcePoint);
            const newTerm: Terminal = {
              id: nanoid(8),
              label: sourceTerm.label || "T",
              polarity: "signal",
              direction: "bidi",
              maxAmp: sourceTerm.maxAmp,
              side: placement.side,
              offset: placement.offset,
              color: sourceTerm.color,
            };
            customizeComponent(toNodeId, { terminals: [...targetDef.terminals, newTerm] });
            toTerminalId = newTerm.id;
          }
          const cableId = addCable({
            fromNodeId,
            fromTerminalId,
            toNodeId,
            toTerminalId,
            lengthM: 0.5,
          });
          useSystemStore.getState().setSelection({ nodeIds: [], cableIds: [cableId] });
          return;
        }

        const snapped = pickSnapTerminal({
          ...sourceInfo,
          targetPoint: dropPoint,
          target,
          targetDef,
          nodes: system.nodes,
          cables: system.cables,
        });
        if (!snapped) return;

        if (sourceIsJ) {
          const cableId = addCable({
            fromNodeId,
            fromTerminalId: "j",
            toNodeId,
            toTerminalId: snapped.id,
            lengthM: 0.5,
          });
          useSystemStore.getState().setSelection({ nodeIds: [], cableIds: [cableId] });
          return;
        }

        setPendingConnection({
          fromNodeId,
          fromTerminalId,
          toNodeId,
          toTerminalId: snapped.id,
        });
        return;
      }

      if (toNodeId) return;
      const clientX = "clientX" in event ? event.clientX : event.changedTouches?.[0]?.clientX;
      const clientY = "clientY" in event ? event.clientY : event.changedTouches?.[0]?.clientY;
      if (clientX == null || clientY == null) return;

      const flowPos = screenToFlowPosition(
        { x: clientX, y: clientY },
        { snapToGrid: false },
      );

      const exclude = fromIdAtStart
        ? cablesTouching(fromIdAtStart, system.cables)
        : new Set<string>();
      const cableHit = findCableHit(
        flowPos,
        exclude,
        flowHitTolerance(CABLE_HIT_TOLERANCE_PX, getZoom()),
      );
      if (cableHit) {
        const result = splitCableWithJunction({
          cableId: cableHit.cableId,
          // Center the 14×14 junction dot on the projected point.
          junctionPos: { x: cableHit.point.x - 7, y: cableHit.point.y - 7 },
          splitFraction: cableHit.fraction,
          newConnection: { fromNodeId, fromTerminalId, lengthM: 0.3 },
        });
        if (result) return;
      }

      const id = addJunction(
        { x: flowPos.x - 7, y: flowPos.y - 7 },
        { provisional: true },
      );
      addCable({
        fromNodeId,
        fromTerminalId,
        toNodeId: id,
        toTerminalId: "j",
        lengthM: 0.3,
      });
      useSystemStore.getState().setSelection({ nodeIds: [id], cableIds: [] });
    },
    [
      screenToFlowPosition,
      getIntersectingNodes,
      getZoom,
      addJunction,
      addCable,
      splitCableWithJunction,
      system,
      setPendingConnection,
      customizeComponent,
    ],
  );

  return {
    cableTargetId,
    cableTargetEdgeId,
    connectionPreview,
    cancelConnectionGesture,
    onConnectStart,
    onConnect,
    onConnectEnd,
  };
}
